import { ColorVariables, type IMenuOptions } from '@group-link-one/gl-ui/sonarprint';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { SettingsRoutes } from '../enums/enums';
import { useMainLayoutTranslate } from './useMainLayoutTranslate';
import { useAuth } from '@/Bridges/GLUtils';
import { IMPERSONATE_KEY } from '@/Constants/keys';
import { usePrivileges } from '@/hooks/usePrivilleges';
import { ALL_ROUTES } from '@/Routes/consts/routes';

export const useMenuOptions = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isImpersonating = localStorage.getItem(IMPERSONATE_KEY);

  const { user, rootToken, setUser, setIsPending, setRootToken, setAccessToken, logOut } =
    useAuth();

  const { features } = usePrivileges();
  const { customLocale } = useMainLayoutTranslate();

  const menuOptions: IMenuOptions[] = [
    {
      icon: 'Users.UserCheck',
      colorText: ColorVariables.BASE.BASE_COLOR_PRIMARY_TEXT,
      text: customLocale('menu.profile', 'Profile'),
      onClick: () => navigate(SettingsRoutes.OVERVIEW),
    },
    {
      icon: 'Settings.Sliders',
      colorText: ColorVariables.BASE.BASE_COLOR_PRIMARY_TEXT,
      text: customLocale('menu.accountSettings', 'Account Settings'),
      onClick: () => navigate(SettingsRoutes.MY_ACCOUNT),
    },
  ];

  if (features.impersonation && !isImpersonating) {
    menuOptions.push({
      icon: 'Audiovisual.Headphones',
      text: customLocale('menu.supportMode', 'Enter in support mode'),
      onClick: () => navigate(ALL_ROUTES.IMPERSONATE),
    });
  }

  if (isImpersonating) {
    menuOptions.push({
      icon: 'Arrows.X',
      text: customLocale('menu.leaveSupportMode', 'Leave support mode'),
      colorText: ColorVariables.WARNING.WARNING_COLOR_TERTIARY_BACKGROUND,
      onClick: () => {
        setUser(undefined);
        setIsPending(true);

        queryClient.clear();

        localStorage.removeItem(IMPERSONATE_KEY);
        setRootToken(rootToken);
        setAccessToken(rootToken);
        setIsPending(false);
      },
    });
  }

  const profileMenuOptions = {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    user: user!,
    menuOptions,
    onLogOut: () => {
      logOut();
      localStorage.removeItem(IMPERSONATE_KEY);
      navigate(ALL_ROUTES.LOGIN_ALT);
    },
  };

  return {
    profileMenuOptions,
  };
};
